<script lang="ts">
  import Modal from '@shared/ui/Modal.svelte';
  import { LOST_REASON_OPTIONS } from '@shared/constants/choices';
  import type { LostReason, SalesAction } from '@shared/types/sales-action';

  interface SolutionOption {
    id: string;
    label: string;
  }

  interface Props {
    open: boolean;
    action: SalesAction;
    solutions: SolutionOption[];
    onConfirm: (
      lostReason: LostReason,
      lostReasonNote: string,
      solutionId: string,
    ) => void;
    onClose: () => void;
  }

  let { open, action, solutions, onConfirm, onClose }: Props = $props();
  const lostReasonOptions = LOST_REASON_OPTIONS as Array<{
    value: LostReason;
    label: string;
  }>;

  // 失注は product_id 必須 (PATCH /status が won/lost で必須を強制)。 既存値を初期選択し、
  // 未設定行でもここで入力させる。 ダイアログは対象ごとに再マウントされるため初期化で十分。
  let solutionId = $state(action.product_id ?? '');
  let lostReason = $state<LostReason | ''>('');
  let lostReasonNote = $state('');
  let touched = $state(false);

  const canConfirm = $derived(
    solutionId !== '' && lostReason !== '' && lostReasonNote.trim() !== '',
  );

  function handleSubmit(ev: SubmitEvent) {
    ev.preventDefault();
    touched = true;
    if (lostReason === '' || !canConfirm) return;
    onConfirm(lostReason, lostReasonNote.trim(), solutionId);
  }
</script>

<Modal {open} title="失注として確定" onClose={onClose}>
  <form id="lost-reason-form" onsubmit={handleSubmit}>
    <p class="context">
      <strong>{action.action_title ?? '(無題)'}</strong> を
      <strong>失注</strong>として確定します。このアクションは完了し、結果タブへ移動します。
    </p>
    <label>
      <span>ソリューション <em>*</em></span>
      <select bind:value={solutionId} data-testid="lost-solution-select">
        <option value="">選択してください</option>
        {#each solutions as s (s.id)}
          <option value={s.id}>{s.label}</option>
        {/each}
      </select>
    </label>
    <label>
      <span>失注理由 <em>*</em></span>
      <select bind:value={lostReason}>
        <option value="">選択してください</option>
        {#each lostReasonOptions as option (option.value)}
          <option value={option.value}>{option.label}</option>
        {/each}
      </select>
    </label>
    <label>
      <span>失注理由メモ <em>*</em></span>
      <textarea bind:value={lostReasonNote} rows="3"></textarea>
    </label>
    {#if touched && !canConfirm}
      <p class="warn">ソリューション・失注理由・失注理由メモは必須です。</p>
    {/if}
  </form>
  {#snippet footer()}
    <button type="button" onclick={onClose}>キャンセル</button>
    <button
      type="submit"
      form="lost-reason-form"
      class="primary"
      disabled={!canConfirm}
    >
      失注として確定
    </button>
  {/snippet}
</Modal>

<style>
  form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .context {
    margin: 0;
    color: #374151;
    font-size: 0.875rem;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  span em {
    color: #b91c1c;
    font-style: normal;
  }
  select,
  textarea {
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    background: white;
  }
  textarea {
    resize: vertical;
  }
  .warn {
    color: #b45309;
    font-size: 0.875rem;
    margin: 0;
  }
  button {
    padding: 8px 16px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
  }
  button.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: white;
  }
  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
</style>
